import React, { useContext } from 'react';
import styles from './ProfileAssetSection.module.css';
import { Table, Spinner } from 'react-bootstrap';
import ProfileSection from '../../components/ProfileSection/ProfileSection';
import ImageIcon from '../../assets/images/button-icons/image-icon.png';
import { RootContext } from '../../hoc/RootContext/RootContext';
import AutocadIcon from './../../assets/images/button-icons/autocad-icon.png';
import { useIntl } from 'react-intl';
import { ASSET_SECTION_LABELS } from '../../constants';
import classNames from 'classnames'
import { NOT_PRESENT } from '../../constants'

export default function ProfileAssetSection({ section, assets, setAssetImagesState, fieldSettings }) {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({id});

  const { assetSchema: schema } = useContext(RootContext);

  assets = assets ?
    assets.filter(asset => asset?.type !== NOT_PRESENT).sort((a, b) => a.unit - b.unit)
    : assets;

  // anytime a new kind of download comes in, it should be added here
  const downloadIcons = { autocad: AutocadIcon };

  const assetTypes = (assets || []).reduce((acc, asset) => {
    if (!acc[asset.type]) acc[asset.type] = [];
    acc[asset.type].push(asset);
    return acc;
  }, {});

  const capitalize = (str) => `${str.charAt(0).toUpperCase()}${str.slice(1)}`;

  // embedded ProfileTable component
  const ProfileTable = ({ section, type, assets, fieldSettings }) => {
    const assetSchema = schema[section.toLowerCase()].find(_ => _.type === type);

    if (!assetSchema) {
      console.warn(`Asset type of ${type} is not in the schema.`);
      return null;
    }

    // filter out ADA related fields from public display
    const fields = assetSchema.fields.filter(_ => {
      return _.id.indexOf('ada-') !== 0 && _.type !== 'label'
    });

    const { hideUnit } = assetSchema;

    const isFieldVisibile = (fieldId) => {
      if (!fieldSettings) return true; // no field settings implies full visibility
      try {
        const isVisible = fieldSettings.assets
          .find(_ => _.section === section.toLowerCase())
          .assets.find(_ => _.type === type)
          .fields.find(_ => _.id === fieldId).visible;
        return !(isVisible === false); // only way to hide an asset is with explicit false value
      } catch {
        return true;
      }
    }

    // renders the 'Images' button, which will open a lightbox with asset images when clicked
    const renderImagesButton = (asset) => {
      if (!asset || !asset.photos || !asset.photos.length) return null;
      return (
        <div className={styles.photosButton} onClick={() => setAssetImagesState({ isOpen: true, images: asset['photos'], index: 0 })}>
          <img src={ImageIcon} alt="Images" />
        </div>
      );
    }

    // renders the headers for assets with a defined tableColumns schema entry
    const renderDefinedHeaders = () => {
      return assetSchema.tableColumns.filter(col => isFieldVisibile(col.id)).map((column, i) => <th key={`header${i}`}>{column.name || capitalize(column.id)}</th>);
    }

    // renders headers for assets dynamically based on their fields
    const renderDynamicHeaders = () => {
      return (
        <>
          {hideUnit ? null : <th key="headerUnit">{t('ui.reference')}</th>}
          {fields.filter(_ => isFieldVisibile(_.id)).map((field, i) =>
            <th key={`header${field.id}-${i}`}>{field.name || capitalize(field.id)}</th>
          )}
          <th>{t('ui.images')}</th>
        </>
      )
    }

    // renders the table data for assets dynamically
    const renderDynamicRows = () => {
      return assets.map(asset =>
        <tr key={asset.guid}>
          {hideUnit ? null : <td key="valueUnit">{asset.unit}</td>}
          {fields.filter(_ => isFieldVisibile(_.id)).map(({ id, type: fieldType }, i) =>
            <td key={`value${id}-${i}`}>{fieldType === 'checkbox' ? asset.attributes[id] ? t('ui.yes') : t('ui.no') : asset.attributes[id]}</td>
          )}
          <td>{renderImagesButton(asset)}</td>
        </tr>
      )
    }

    // renders the table data for assets with a defined tableColumns schema entry
    const renderDefinedRows = () => {
      // determines what to display as the value based on tableColumns
      const renderValue = ({ id, staticValue, name, type }, asset) => {
        if (staticValue) return staticValue;
        if (assetSchema.fields[id] === 'checkbox') return asset.attributes[id] ? t('ui.yes') : t('ui.no');
        if (type === 'download') {
          let uploadSchema = assetSchema.uploads.find(_ => _.id === id);
          const downloadIcon = downloadIcons[id];
          if (!downloadIcon) console.warn('Icon not found for download type ', id);
          return uploadSchema && asset[id] ? <div className={styles.downloadButton}><a href={asset[id].location} download target="blank"><img src={downloadIcon} alt={name || capitalize(id)} /></a></div> : null;
        }
        if (id === 'photos') {
          return renderImagesButton(asset);
        }
        if (id === 'unit') {
          return `${assetSchema.unitText || 'Reference'} ${asset.unit}`;
        }
        return asset.attributes[id];
      }

      return assets.map(asset =>
        <tr key={`row${asset.guid}`}>
          {assetSchema.tableColumns.filter(_ => isFieldVisibile(_.id)).map((column, i) =>
            <td key={`value${asset.guid}-${i}`}>
              {renderValue(column, asset)}
            </td>
          )}
        </tr>
      )
    }

    return (
      <div key={ `assetSection${ type }` }>
        <div className={ classNames(styles.typeHeader, 'pb-3') }>
          { assetSchema.tableName || type }
        </div>
        <Table borderless hover responsive size="sm" className={ styles.assetTable }>
          <thead>
            <tr className="border-bottom">
              { assetSchema.tableColumns ? renderDefinedHeaders() : renderDynamicHeaders() }
            </tr>
          </thead>
          <tbody>
            { assetSchema.tableColumns ? renderDefinedRows() : renderDynamicRows() }
          </tbody>
        </Table>
      </div>
    )
  }

  const isAssetVisibile = (type) => {
    if (!fieldSettings) return true; // no field settings implies full visibility
    try {
      const isVisible = fieldSettings.assets.find(_ => _.section === section.toLowerCase()).assets.find(_ => _.type === type).visible;
      return !(isVisible === false); // only way to hide an asset is with explicit false value
    } catch {
      return true;
    }
  }

  const assetTypeKeys = Object.keys(assetTypes).filter(isAssetVisibile);

  // render the ProfileSection
  return (
    <ProfileSection section={section} label={ASSET_SECTION_LABELS[section.split('_').join(' ')]}>
      {assetTypeKeys.length && assets?.length ?
        <div className={styles.assetTableSection}>
          {
            schema ? assetTypeKeys.map((type) => {
              return (
                <ProfileTable
                  key={ `${ section }-${ type }` }
                  type={ type }
                  section={ section }
                  assets={ assetTypes[ type ] }
                  fieldSettings={ fieldSettings }
                />
              )
            })
              : <Spinner animation="grow" variant="primary" />
          }
        </div>
        : <div>{t('ProfileAssetSection.noData')}</div>
      }
    </ProfileSection>
  );
}
